import React, { useEffect } from 'react';
import { graphql } from 'gatsby';

import useSiteMetadata from '@hooks/useSiteMetadata';

import Layout from '@src/layouts';
import SEO from '@commons/SEO';
import { StandardSection } from '@commons/standard-section/StandardSection';
import { HTMLRender } from '@commons/html-render/HTMLRender';
import { ReadingProgress } from '@commons/reading-progress/ReadingProgress';
import NewsletterBanner from '@commons/banner-newsletter/NewsletterBanner';
import { CategoryBrowser } from '@commons/category-browser/CategoryBrowser';

import Header from './components/header/Header';
import ShareIcons from './components/share-icons/ShareIcons';
import AuthorsDetails from './components/authors-details/AuthorsDetails';
import RelatedPosts from './components/related-posts/RelatedPosts';
import AsidePanel from './components/aside-panel/AsidePanel';
import { arrowIcon } from './components/arrowIcon';
// import { NewRustPopup } from './components/rust-popup/RustPopup';

// import { rustBlogposts } from './payload';

import * as styles from './blog-post.module.scss';

export default function BlogPostTemplate(props) {
  const { siteURL } = useSiteMetadata();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const blogPostUrl = typeof window !== 'undefined' ? window.location.href : '';

  // const [shouldShowRustExitIntentPopup, setShouldShowRustExitIntentPopup] = useState(rustBlogposts.includes(pathname.slice(6, -1)), );

  const {
    markdownRemark: {
      frontmatter: {
        title,
        date,
        thumbnail,
        authors,
        relatedPosts,
        seoDescription,
        seoTitle,
        featuredImage,
        blogHeaderImage,
        postCategories,
      },
      html,
      wordCount: { words },
    },
  } = props.data;

  const authorsForSchema = [];

  for (let i = 0; i < authors.length; i++) {
    const item = {
      '@type': 'person',
      name: `${authors[i].frontmatter.name} ${authors[i].frontmatter.surname}`,
      jobTitle: authors[i].frontmatter.position,
    };
    authorsForSchema.push(item);
  }

  const { articleSection, mainContent, innerWrapper, outerWrapper } = styles;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: seoTitle,
    description: seoDescription,
    datePublished: date,
    datemodified: date,
    mainEntityOfPage: siteURL + pathname,
    image: {
      '@type': 'imageObject',
      url: siteURL + featuredImage.publicURL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CodiLime',
      url: siteURL,
      logo: {
        '@type': 'imageObject',
        url: 'https://codilime.com/static/logo-header-c04655d04aad374f649cc5e98efb1fc2.svg',
      },
    },
    author: authorsForSchema,
  };

  const isThereTOC = html.includes('table-of-contents');
  const splitHTML = html.split('</div>');
  const tocContent = splitHTML.shift() + '</div>';
  const content = isThereTOC ? splitHTML.join('</div>') : html;

  useEffect(() => {
    const toc = document.getElementsByClassName('table-of-contents')[0];
    if (toc) {
      const tocHeader = document.createElement('h4');
      tocHeader.innerHTML = arrowIcon;
      tocHeader.classList = 'toc-header';
      toc.addEventListener('click', () => {
        toc.classList.remove('open');
      });
      tocHeader.addEventListener('click', (e) => {
        e.stopPropagation();
        toc.classList.toggle('open');
      });
      toc.insertBefore(tocHeader, toc.children[0]);
      const listItems = Array.from(toc.getElementsByTagName('a'));
      listItems.forEach((listItem) => {
        const text = listItem.innerText;
        const maxPossibleLength = 60;
        const safetyMargin = 10;
        if (text.length > maxPossibleLength) {
          const gapIndex =
            text.slice(maxPossibleLength - safetyMargin).search(/[^\w]/) + maxPossibleLength - safetyMargin;
          listItem.innerText = text.slice(0, gapIndex) + '...';
        }
      });
      listItems[0].classList.add('current-anchor');
      listItems.forEach((listItem, index) => {
        index > 2 && listItem.classList.add('hidden');
      });
      const ids = listItems.map((listItem) => listItem.getAttribute('href')?.slice(1));
      const targets = ids.map((id) => document.getElementById(id));
      const observerCallback = (changedObserved) => {
        const intersectingElement = changedObserved.find((el) => el.isIntersecting);
        const id = intersectingElement && intersectingElement.target.id;
        if (id) {
          const currentAnchorIndex = ids.indexOf(id);
          const l = listItems.length;
          //set currentAnchor styling
          listItems.forEach((listItem) => listItem.classList.remove('current-anchor'));
          listItems[currentAnchorIndex].classList.add('current-anchor');
          //set what's visible
          listItems.forEach((listItem) => listItem.classList.add('hidden'));
          if (currentAnchorIndex < 2) {
            [0, 1, 2].forEach((el) => listItems[el].classList.remove('hidden'));
          } else if (currentAnchorIndex > l - 3) {
            [l - 1, l - 2, l - 3].forEach((el) => listItems[el]?.classList.remove('hidden'));
          } else
            [currentAnchorIndex - 1, currentAnchorIndex, currentAnchorIndex + 1].forEach((el) => {
              listItems[el]?.classList.remove('hidden');
            });
        }
      };
      const observer = new IntersectionObserver(observerCallback, { rootMargin: '50px 0px -300px 0px' });
      targets.forEach((target) => {
        target && observer.observe(target);
      });
    }
  }, []);

  return (
    <>
      {/* {shouldShowRustExitIntentPopup && <NewRustPopup close={() => setShouldShowRustExitIntentPopup(false)} />} */}
      <Layout {...props}>
        <SEO
          title={title}
          seoDescription={seoDescription}
          seoTitle={seoTitle}
          featuredImage={siteURL + featuredImage.publicURL}
          date={date}
          schemaMarkup={schema}
        />
        <Header
          categories={postCategories}
          words={words}
          thumbnail={blogHeaderImage ? blogHeaderImage : thumbnail}
          date={date}
          title={title}
        />
        <ReadingProgress targetEl="#reading-progress-target" />
        <StandardSection className={articleSection} classNames={{ innerWrapper, outerWrapper }}>
          <div className={mainContent}>
            <article id="reading-progress-target">
              <HTMLRender content={content} />
              <ShareIcons title={title} blogPostUrl={blogPostUrl} />
            </article>
            {authors && <AuthorsDetails authors={authors} />}
            {relatedPosts && <RelatedPosts posts={relatedPosts} />}
          </div>
          <AsidePanel>
            {isThereTOC && <HTMLRender content={tocContent} />}
            <NewsletterBanner subtitleLess />
          </AsidePanel>
        </StandardSection>
        <CategoryBrowser isBottom />
      </Layout>
    </>
  );
}

export const blogQuery = graphql`
  query BlogPostByPath($path: String!) {
    markdownRemark(frontmatter: { path: { eq: $path } }) {
      frontmatter {
        date
        path
        title
        seoDescription
        seoTitle
        featuredImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
          extension
          publicURL
        }
        blogHeaderImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
          extension
          publicURL
        }
        thumbnail {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
          extension
          publicURL
        }
        authors {
          frontmatter {
            avatar {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
              extension
              publicURL
            }
            name
            surname
            bio
            position
          }
        }
        relatedPosts {
          frontmatter {
            path
            title
            thumbnail {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
              extension
              publicURL
            }
            authors {
              frontmatter {
                avatar {
                  childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                  }
                  extension
                  publicURL
                }
                name
                surname
              }
            }
          }
          wordCount {
            words
          }
        }
        postCategories {
          frontmatter {
            postCategoryName
          }
        }
      }
      html
      wordCount {
        words
      }
    }
  }
`;
